<template>
  <screen
    :screen-info="headText"
    :can-skip="false"
    :can-leave="!connecting"
    @leave="cancel"
    no-padding
    scrollable
  >
    <!-- Scanning Area -->
    <div v-if="!scanDone" class="scan-area-outer relative">
      <div class="scan-area-inner animate"></div>
      <div class="scan-area-inner icon-container">
        <img class="absolute centered" src="../assets/Discovery/bolt.svg" alt="Scanning" />
        <div
          v-for="(device, idx) in redactedList"
          class="absolute"
          :key="idx"
          :style="getStyle(idx)"
        >
          <img
            v-if="device.type === 'foot'"
            src="../assets/Discovery/foot_scanner_icon.svg"
            :alt="device.name"
          />
          <img v-else src="../assets/Discovery/body_scanner_icon.svg" :alt="device.name" />
        </div>
      </div>
    </div>
    <!-- Device List -->
    <div v-else class="w-full">
      <!-- Device Card -->
      <slider @mounted="initSlider">
        <slide v-for="(device, idx) in deviceList" :key="idx">
          <scanner-card
            :scanner="device"
            @click="connectToDevice(idx)"
            @purchase="togglePurchaseCart"
          />
        </slide>
      </slider>
      <c-button class="mt-10 mx-auto" @click="connectToDevice(sliderIndex)">
        <span>{{ $t('discovery.start_scan') }}</span>
      </c-button>
    </div>
    <!-- Prompt -->
    <rounded-card
      v-if="showCreditsPrompt"
      show-right-btn
      class="p-3 z-40"
      @set-close-status="closeCreditsPrompt"
    >
      <div class="px-6 p-y pt-0 text-center flex flex-1 flex-col items-center">
        <!-- No Credits -->
        <template>
          <h3 class="text-xl whitespace-pre">
            {{ $t('mobileScan.no_credits') }}
          </h3>
          <img
            class="my-auto"
            width="170"
            src="../assets/Discovery/buy_credits.svg"
            alt="Buy Credits"
          />
          <c-button class="my-auto" @click="togglePurchaseCart">
            <span>{{ $t('mobileScan.purchase') }}</span>
          </c-button>
        </template>
      </div>
    </rounded-card>
    <rounded-card v-if="showPrompt" show-right-btn class="p-3" @set-close-status="cancel">
      <div class="px-6 p-y pt-0 text-center flex flex-1 flex-col items-center">
        <!-- Enable BT -->
        <template v-if="!isBluetoothAvailable">
          <h3 class="text-xl">
            {{ $t('discovery.turn_on_bt_hint') }}
          </h3>
          <p v-if="isIOS" class="text-m bt-hint" v-html="$t('discovery.turn_on_bt_hint_ios')"></p>
          <p v-else class="text-m bt-hint" v-html="$t('discovery.turn_on_bt_hint_android')"></p>
          <img class="my-auto py-10" src="../assets/Discovery/enable_bt.svg" alt="Enable Bluetooth" />
        </template>
        <!-- No Scanners -->
        <template v-else>
          <h3 class="text-xl">
            {{ $t('discovery.no_scanners') }}
          </h3>
          <c-button class="my-auto" @click="startDeviceScan">
            <span>{{ $t('discovery.try_again') }}</span>
          </c-button>
        </template>
      </div>
    </rounded-card>
    <rounded-card
      v-if="showForceReleasePrompt"
      :head-text="$t('discovery.scanner_is_busy_title')"
      show-right-btn class="p-3"
      @set-close-status="closeForceReleasePrompt">
      <div class="px-6 p-y pt-0 text-center flex flex-1 flex-col items-center">
        <h3 class="text-m mb-5">
          {{ $t('discovery.scanner_is_busy_desc') }}
        </h3>
        <div>
          <c-button class="my-5" @click="forceRelease">
            <span>{{ $t('discovery.scanner_is_busy_force_release') }}</span>
          </c-button>
          <c-button secondary class="my-5" @click="cancel">
            <span>{{ $t('discovery.scanner_is_busy_wait') }}</span>
          </c-button>
        </div>
      </div>
    </rounded-card>
    <unsupported-device v-if="showMobileScanIncapable" @close="showMobileScanIncapable = false" />
    <qr-scan
      v-show="connecting"
      :steps="2"
      :step="step"
      :message="connectionHint"
      :can-leave="false"
      @done="done"
      @cancel="reset"
    />
    <PurchaseCart v-show="showPurchaseCart" @close="togglePurchaseCart" @refresh="getUserCredit" />
  </screen>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import op from 'object-path';
import ApiActions from '@/modules/api-actions';
import { EVENTS as HW_EVENTS } from '@/events/hardware';
import page from '@/mixins/page';
import audioPlayer from '@/mixins/audio-player';
import hasSlider from '@/mixins/has-slider';
import scannerError from '@/mixins/scanner-error';
import ENVS from '@/modules/envs';
import ScanProcess from '@/modules/scan-process';
import WebBT from '@/modules/web-bluetooth'; // eslint-disable-line
import { wait } from '@/modules/utils';
import { SET_SCANNER_ID } from '@/store/modules/hardware';
import i18n from '@/i18n';
import Screen from '@/components/common/Screen.vue';
import QrScan from '@/components/Welcome/QrScan.vue';
import RoundedCard from '@/components/common/RoundedCard.vue';
import UnsupportedDevice from '@/components/common/UnsupportedDevice.vue';
import CButton from '@/components/common/CButton.vue';
import ScannerCard from '@/components/common/ScannerCard.vue';
import Slider from '@/components/common/Slider.vue';
import Slide from '@/components/common/Slide.vue';
import PurchaseCart from '@/components/MobileScan/Purchase/Purchase.vue';

const isFoot = d => {
  const { type } = d;
  return !!parseInt(type, 10);
};
const isMobileScan = d => d.type === 'mobile';
const isTG3DMobileScan = d => d.provider === 10 && d.type === 'mobile';

const MOCK_SCANNERS = [
  { id: 'BodyScanner-Mock', name: 'BodyScanner-Mock', type: 'body' },
  { id: 'FootScanner-Mock', name: 'FootScanner-Mock', type: 'foot' },
];

export default {
  name: 'Discovery',
  mixins: [page, audioPlayer, hasSlider, scannerError],
  components: {
    Screen,
    RoundedCard,
    CButton,
    ScannerCard,
    QrScan,
    Slider,
    Slide,
    UnsupportedDevice,
    PurchaseCart,
  },
  data: () => ({
    headText: i18n.t('discovery.searching'),
    showPrompt: false,
    showCartPrompt: false,
    showCreditsPrompt: false,
    showForceReleasePrompt: false,
    showPurchaseCart: false,
    scanDone: false,
    deviceList: [],
    connecting: false,
    step: 0,
    connectionHint: '',
    footScannerConnected: false,
    showMobileScanIncapable: false,
    armsRangeSet: false,
  }),
  mounted() {
    if (this.hasFixedScanner) {
      this.useFixedScanner();
      // Initialize BT module
      if (!WebBT.enabled) {
        this.$events.emit(HW_EVENTS.START_BT_SCAN, true);
      }
      return;
    }
    this.$events.on(HW_EVENTS.BT_DEVICE_CHANGED, this.updateDeviceList);
    this.startDeviceScan();
    this.$store.commit(`hardware/${SET_SCANNER_ID}`, '');
    if (this.canUseMobileScan) {
      this.playAudio('ms-01-welcome', true);
    } else {
      this.playAudio('1-Welcome', true);
    }
  },
  beforeDestroy() {
    this.$events.off(HW_EVENTS.BT_DEVICE_CHANGED, this.updateDeviceList);
  },
  methods: {
    ...mapActions('hardware', ['toggleArmDetection', 'setArmsRange']),
    togglePurchaseCart() {
      this.showCreditsPrompt = false;
      this.showPurchaseCart = !this.showPurchaseCart;
      this.sliderIndex = 0;
    },
    startDeviceScan() {
      this.headText = this.$t('discovery.searching');
      this.deviceList = [];
      if (ENVS.mock) {
        this.deviceList = [...MOCK_SCANNERS];
      }
      this.sliderIndex = 0;
      this.scanDone = false;
      this.closePrompt();
      this.$events.emit(HW_EVENTS.START_BT_SCAN, true);
      wait(3).then(() => {
        this.addMobileScan();
        this.scanDone = true;
      });
    },
    useFixedScanner() {
      this.sliderIndex = 0;
      this.deviceList.unshift({
        name: this.fixedScannerId,
        type: this.fixedScannerType || 'body',
        id: this.fixedScannerId,
        key: this.fixedSessionKey,
      });
      this.scanDone = true;
      this.connectToDevice(0);
    },
    addMobileScan() {
      if (!this.canUseMobileScan || this.mobileScanInList) return;

      this.getUserCredit();
      const { provider, scannerId, sessionKey, scannerToken } = this.mobileScannerConfigs;
      this.deviceList.unshift({
        name: this.storeName || 'iPhone',
        type: 'mobile',
        provider,
        id: scannerId,
        key: sessionKey,
        token: scannerToken,
      });
      if (this.slideCount > 1) {
        this.sliderIndex = this.sliderIndex > 0 ? this.sliderIndex : 1;
      }
    },
    waitForArmRangesSet(timeout) {
      if (this.armsRangeSet || !this.connecting) {
        return Promise.resolve();
      }
      if (timeout === 0) {
        return Promise.reject(new Error('[BT]CONNECTION_TIMEOUT'));
      }
      return wait(1).then(() => this.waitForArmRangesSet(timeout - 1));
    },
    handleArmDetection({ data }) {
      if (!Array.isArray(data) || data.length < 2) {
        console.log('handleArmDetection: corrupted data', data);
        return;
      }

      if (data.length === 4) {
        this.setArmsRange(data);
        this.armsRangeSet = true;
        this.$events.off(HW_EVENTS.BT_DATA, this.handleArmDetection);
      }
    },
    async connectToDevice(index) {
      const device = this.deviceList[this.sliderIndex];
      if (!device || index !== this.sliderIndex) return;

      console.log('connecting to device', device);
      try {
        if (isTG3DMobileScan(device)) {
          this.$events.startLoading();
        } else if (isMobileScan(device)) {
          if (!this.isMobileScanCapable) {
            this.showMobileScanIncapable = true;
            this.playAudio('ms-03-unsupported', true);
            return;
          }
          if (this.chargeOnUser && !this.credits) {
            this.showCreditsPrompt = true;
            return;
          }
          this.$events.startLoading();
        } else {
          this.connecting = true;
          this.step = 1;
          this.connectionHint = i18n.t('welcome.scan_code_connect_to_scanner');
          if (WebBT.enabled && !ENVS.mock) {
            await WebBT.requestDevice(device.id).catch(e =>
              Promise.reject(new Error(`[BT]${e.message}`))
            );
          }
        }
        console.log('initializing scanner', device.id, device.key);
        const { provider, id, key, type, token } = device;
        await ScanProcess.cancel();
        await ScanProcess.initial({ provider, id, key, type, token }, this.chargeOnUser);
        this.$store.commit(`hardware/${SET_SCANNER_ID}`, id);
        if (isMobileScan(device)) {
          this.$events.stopLoading();
          this.$events.goto(provider === 10 ? 'MobileScan2/UserProfile' : 'MobileScan/UserHeight');
          return;
        }
        if (type === 'foot') {
          this.footScannerConnected = true;
          this.playAudio('foot_begin', true);
        } else {
          if (!ScanProcess.withHandles() && !ENVS.mock) {
            this.toggleArmDetection(true);
            this.$events.on(HW_EVENTS.BT_DATA, this.handleArmDetection);
            this.$events.emit(HW_EVENTS.START_ARM_DETECTION, id);
            await this.waitForArmRangesSet(15);
          }
          this.playAudio('4-Ready_to_go', true);
        }
        this.step = 2;
        this.connectionHint = i18n.t('welcome.scan_code_done');
        ScanProcess.reportEvent('ble_connected');
      } catch (e) {
        if (isMobileScan(device)) {
          this.$events.stopLoading();
        }
        if (e.message.indexOf('[BT]') > -1) {
          ScanProcess.reportEvent('ble_error');
          console.log(`[ERROR] Discovery BT error: ${e.message}`);
          this.$events.alert(this.$t('generic.bluetooth_error'));
        } else {
          const { errno, msg } = op.get(e, 'response.data.error', {});
          if (errno === 40303) {
            this.showForceReleasePrompt = true;
          } else {
            console.log(`[ERROR] Discovery initializeScan error: ${errno}, ${msg || e.message}`);
            const errorMessage = `${this.getScannerErrorHint(errno, device.type)}${
              errno ? `<br>(${errno})` : ''
            }`;
            this.$events.alert(errorMessage);
          }
        }
        this.reset();
      }
    },
    done() {
      if (!this.connecting) return;

      if (this.footScannerConnected) {
        this.$events.goto('FootScan/ThingsToNote');
      } else {
        this.$events.goto('ThingsToNote');
      }
    },
    reset() {
      this.step = 0;
      this.toggleArmDetection(false);
      if (ScanProcess.isOngoing()) {
        if (!ScanProcess.withHandles()) {
          this.$events.emit(HW_EVENTS.STOP_ARM_DETECTION, ScanProcess.scannerId);
        }
        ScanProcess.cancel().then(() => {
          this.connecting = false;
        });
      } else {
        this.connecting = false;
      }
    },
    updateDeviceList(devices) {
      if (ENVS.mock) return;

      this.deviceList = devices.map(d => ({ ...d, type: isFoot(d) ? 'foot' : 'body' }));
      this.addMobileScan();
      console.log('updateDeviceList', this.deviceList);
    },
    showBTSettings() {
      this.$events.emit(HW_EVENTS.SHOW_BT_SETTINGS);
    },
    async forceRelease() {
      const device = this.deviceList[this.sliderIndex];
      if (!device) return;

      console.log('forceRelease scanner', device.id);
      this.$events.startLoading();
      try {
        await ScanProcess.forceRelease(device.id);
        this.closeForceReleasePrompt();
        this.connectToDevice(this.sliderIndex);
      } catch (e) {
        console.log(e.message);
        const status = op.get(e, 'response.status', 0);
        const errno = op.get(e, 'response.data.error.errno', 0) || status;
        const errorMessage = `${this.$t('welcome.unexpected_error')}${errno ? `<br>(${errno})` : ''}`;
        this.$events.alert(errorMessage);
      } finally {
        this.$events.stopLoading();
      }
    },
    closePrompt() {
      this.showPrompt = false;
    },
    closeCreditsPrompt() {
      this.showCreditsPrompt = false;
    },
    closeForceReleasePrompt() {
      this.showForceReleasePrompt = false;
    },
    cancel() {
      this.$events.emit(HW_EVENTS.STOP_BT_SCAN);
      if (this.showPrompt && !this.isBluetoothAvailable && this.mobileScanInList) {
        this.closePrompt();
        return;
      }
      this.closePrompt();
      console.log('cancel discovery');
      this.$events.goto(this.isContactless ? 'Welcome' : 'HomePage');
    },
    getStyle(index) {
      /** to show the devices on scanning area like this:
       0 2
       3 1
       * */
      const offset = '15%';
      const atTop = index % 2 === 0;
      const atLeft = index === 0 || index === 3;
      return {
        [atTop ? 'top' : 'bottom']: offset,
        [atLeft ? 'left' : 'right']: offset,
      };
    },
    async getUserCredit() {
      try {
        await ApiActions.user.getUserCredit();
      } catch (e) {
        console.log('getUserCredit', e);
      }
    },
  },
  computed: {
    ...mapState('mtmUser', ['fixedScannerId', 'fixedScannerType', 'fixedSessionKey']),
    ...mapGetters('hardware', ['isBluetoothAvailable', 'isMobileScanCapable']),
    ...mapGetters('app', ['canUseMobileScan', 'storeName', 'chargeOnUser', 'mobileScannerConfigs', 'isWeb']),
    ...mapGetters('mtmUser', ['isContactless', 'hasFixedScanner']),
    ...mapGetters('user', ['credits']),
    redactedList() {
      return this.deviceList.slice(0, 4);
    },
    slideCount() {
      return this.deviceList.length;
    },
    mobileScanInList() {
      return !!this.deviceList.find(isMobileScan);
    },
  },
  watch: {
    isBluetoothAvailable: {
      handler(nv) {
        if (this.hasFixedScanner) return;

        this.showPrompt = !nv;
        if (nv) {
          this.startDeviceScan();
        }
      },
      immediate: true,
    },
    slideCount(nv) {
      if (this.hasFixedScanner) return;

      const newLastIndex = nv - 1;
      this.sliderIndex = Math.min(newLastIndex, this.sliderIndex);
      this.resetSlider();
      this.showPrompt = nv === 0 || (!this.isBluetoothAvailable && !this.isWeb);
    },
    scanDone(nv, ov) {
      if (!ov && nv && this.slideCount > 0) {
        if (this.canUseMobileScan) {
          this.playAudio('ms-02-select_nearby', true);
        } else {
          this.playAudio('2-Select_scanner', true);
        }
        this.headText = this.$t('discovery.select_scanner');
      } else if (this.slideCount === 0) {
        this.showPrompt = true;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.scan-area-outer {
  width: 80%;
  max-width: 500px;
  background-image: linear-gradient(to right, #540DEB, #2245FF);
  border-radius: 100%;
  position: relative;
  overflow: visible;
  &:after {
    content: '';
    display: block;
    padding-bottom: 100%;
  }
}
.scan-area-inner {
  @apply w-full h-full rounded-full;
  &.animate {
    animation: scanning 3s backwards infinite;
    opacity: 0.1;
    background-color: #f5f5f5;
  }
}
.icon-container {
  @apply absolute top-0 left-0;
  padding: 15%;
}
.force-width {
  max-width: 300px !important;
  min-width: 300px !important;
}
@keyframes scanning {
  0% {
    transform: scale(0.1);
  }
  100% {
    transform: scale(1);
  }
}
.bt-hint {
  text-align: left;
  margin: 1rem 1rem 0rem 1rem;
  line-height: 2rem;
}
</style>
